// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // XNG-Change
    dataTableRow?: DataTableRowProps;
    dataTableHeadCell?: DataTableCellProps;
    dataTableCell?: DataTableCellProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes, detail } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    // XNG-Change: change structure to match design
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            {/* XNG-Change: remove brand */}
            <DataTable>
                <DataTableBody>
                    {attributeTypes?.map((attributeType, index) => {
                        if (index % 2) {
                            return;
                        }
                        return (
                            <React.Fragment key={attributeType.id.toString()}>
                                <DataTableRow {...styles.dataTableRow} key={`${attributeType.id.toString()}_even`}>
                                    <DataTableCell {...styles.dataTableHeadCell} key={`${attributeType.id.toString()}_even_cell_1`}>
                                        {attributeType.label || attributeType.name}
                                    </DataTableCell>
                                    {index + 1 < attributeTypes.length ? (
                                        <DataTableCell {...styles.dataTableHeadCell} key={`${attributeType.id.toString()}_even_cell_2`}>
                                            {attributeTypes[index + 1].label || attributeTypes[index + 1].name}
                                        </DataTableCell>
                                    ) : (
                                        <DataTableCell {...styles.dataTableHeadCell} key={`${attributeType.id.toString()}_even_cell_2`}></DataTableCell>
                                    )}
                                </DataTableRow>
                                <DataTableRow {...styles.dataTableRow} key={`${attributeType.id.toString()}_odd`}>
                                    <DataTableCell {...styles.dataTableCell} key={`${attributeType.id.toString()}_odd_cell_1`}>
                                        {" "}
                                        <span data-test-selector="attributes_item_value">
                                            {(attributeType.attributeValues ?? [])
                                                .map(attributeValue => attributeValue.valueDisplay)
                                                .join(", ")}
                                        </span>
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell} key={`${attributeType.id.toString()}_odd_cell_2`}>
                                        {index + 1 < attributeTypes.length ? (
                                            <span data-test-selector="attributes_item_value">
                                                {(attributeTypes[index + 1].attributeValues ?? [])
                                                    .map(attributeValue => attributeValue.valueDisplay)
                                                    .join(", ")}
                                            </span>
                                        ) : (
                                            <span></span>
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            </React.Fragment>
                        );
                    })}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
